import { useEffect } from 'react';

// Потолок одновременно живущих частиц — чтобы клики/ховеры по плотным
// таблицам кнопок не заваливали DOM сотнями нод и не лагали.
const MAX_PARTICLES = 48;
let activeParticles = 0;

const CHARS        = ['♥', '✦', '✧', '✨', 'мяу~', 'にゃ~', '💫', '♡', '✦', '✧', '★'];
const SPARK_CHARS  = ['✦', '✧', '⋆', '·', '✦', '★', '✧'];
const COLORS       = ['#ff6b9d', '#3a9d6a', '#ff85a9', '#ffd060', '#c084fc', '#ec4899', '#34d399'];
const SPARK_COLORS = ['#3a9d6a', '#a7f3c8', '#34d399', '#ffd060', '#ff85a9'];

// Расширенный список интерактивных элементов — всё, на что можно жать
const INTERACTIVE_SELECTOR = [
  'button:not([disabled])',
  'a[href]',
  '[role="button"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[role="option"]',
  '[role="tab"]',
  '[role="menuitem"]',
  '[role="switch"]',
  '[role="treeitem"]',
  '[role="combobox"]',
  'input[type="checkbox"]',
  'input[type="radio"]',
  'input[type="range"]',
  'input[type="color"]',
  'select',
  'label',
  '.MuiIconButton-root',
  '.MuiChip-root',
  '.MuiListItemButton-root',
  '.MuiMenuItem-root',
  '.MuiTab-root',
  '.rd-page-head__link',
  '.rd-icon-btn',
].join(', ');

function fireParticle(x, y) {
  if (activeParticles >= MAX_PARTICLES) return;
  activeParticles++;
  const el    = document.createElement('span');
  const char  = CHARS[Math.floor(Math.random() * CHARS.length)];
  const color = COLORS[Math.floor(Math.random() * COLORS.length)];
  const size  = 11 + Math.random() * 14;
  const angle = (Math.random() - 0.5) * 150;
  const dist  = 55 + Math.random() * 90;
  const dx    = Math.sin((angle * Math.PI) / 180) * dist;
  const dy    = -(dist * 0.55 + Math.random() * 50);

  el.textContent = char;
  Object.assign(el.style, {
    position:    'fixed',
    left:        `${x}px`,
    top:         `${y}px`,
    fontSize:    `${size}px`,
    color,
    pointerEvents: 'none',
    zIndex:      '99998',
    transform:   'translate(-50%, -50%)',
    userSelect:  'none',
    fontWeight:  'bold',
    textShadow:  `0 0 8px ${color}99`,
    lineHeight:  '1',
  });

  document.body.appendChild(el);

  el.animate(
    [
      {
        transform: 'translate(-50%, -50%) scale(0) rotate(0deg)',
        opacity: 0,
      },
      {
        transform: `translate(calc(-50% + ${dx * 0.35}px), calc(-50% - 12px)) scale(1.4) rotate(${angle * 0.3}deg)`,
        opacity:   1,
        offset:    0.22,
      },
      {
        transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) scale(0.5) rotate(${angle}deg)`,
        opacity:   0,
      },
    ],
    {
      duration: 650 + Math.random() * 450,
      easing:   'cubic-bezier(0.22, 0.61, 0.36, 1)',
      fill:     'forwards',
    }
  ).finished.then(() => { el.remove(); activeParticles--; });
}

const BTN_SELECTOR = 'button:not([disabled]), .rd-page-head__link, .MuiButton-contained:not([disabled])';

function fireSparkFromBranch(btnEl) {
  const rect = btnEl.getBoundingClientRect();
  const count = 3 + Math.floor(Math.random() * 3);
  for (let i = 0; i < count; i++) {
    setTimeout(() => {
      if (activeParticles >= MAX_PARTICLES) return;
      activeParticles++;
      // Случайно с левой или правой стороны веточки
      const side = Math.random() > 0.5 ? 1 : -1;
      const x = rect.left + (side === 1 ? rect.width + 14 : -14) + (Math.random() - 0.5) * 10;
      const y = rect.top + rect.height / 2 + (Math.random() - 0.5) * rect.height * 0.6;

      const el    = document.createElement('span');
      const char  = SPARK_CHARS[Math.floor(Math.random() * SPARK_CHARS.length)];
      const color = SPARK_COLORS[Math.floor(Math.random() * SPARK_COLORS.length)];
      const size  = 8 + Math.random() * 8;
      const dx    = side * (20 + Math.random() * 30);
      const dy    = -(15 + Math.random() * 35);

      el.textContent = char;
      Object.assign(el.style, {
        position:    'fixed',
        left:        `${x}px`,
        top:         `${y}px`,
        fontSize:    `${size}px`,
        color,
        pointerEvents: 'none',
        zIndex:      '99999',
        transform:   'translate(-50%, -50%)',
        userSelect:  'none',
        textShadow:  `0 0 6px ${color}`,
        lineHeight:  '1',
      });

      document.body.appendChild(el);
      el.animate(
        [
          { transform: 'translate(-50%, -50%) scale(0)', opacity: 0 },
          { transform: `translate(calc(-50% + ${dx * 0.4}px), calc(-50% + ${dy * 0.4}px)) scale(1.3)`, opacity: 1, offset: 0.3 },
          { transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) scale(0.3)`, opacity: 0 },
        ],
        { duration: 500 + Math.random() * 300, easing: 'ease-out', fill: 'forwards' }
      ).finished.then(() => { el.remove(); activeParticles--; });
    }, i * 70);
  }
}

export default function useCatClickEffect(active) {
  useEffect(() => {
    if (!active) return;

    // Искорки из веточек при hover на кнопках.
    // Раньше: два слушателя mouseenter/mouseleave в capture-фазе (срабатывают
    // на каждом элементе под курсором) + две волны искр на каждое наведение.
    // Проводишь мышкой по таблице с кнопками — десятки нод в секунду, лимит
    // частиц выбирается мгновенно и всё лагает.
    // Теперь: один всплывающий mouseover, искры только если курсор реально
    // задержался на кнопке (HOVER_DWELL_MS), одна волна вместо двух, и
    // повторное срабатывание на той же кнопке не запускается.
    const HOVER_DWELL_MS = 150;
    let hoveredBtn = null;
    let dwellTimer = null;

    const handleMouseOver = (e) => {
      const el = e.target;
      if (!el || typeof el.closest !== 'function') return;
      const btn = el.closest(BTN_SELECTOR);
      if (btn === hoveredBtn) return;   // всё ещё та же кнопка — не перезапускаем

      clearTimeout(dwellTimer);
      hoveredBtn = btn;
      if (!btn) return;

      dwellTimer = setTimeout(() => {
        // Кнопка могла пропасть из DOM за время задержки
        if (hoveredBtn === btn && btn.isConnected) fireSparkFromBranch(btn);
      }, HOVER_DWELL_MS);
    };

    document.addEventListener('mouseover', handleMouseOver);

    const handleClick = (e) => {
      const el = e.target;
      // Не стреляем на текстовых полях ввода
      const tag  = el.tagName;
      const type = (el.type || '').toLowerCase();
      if (tag === 'TEXTAREA') return;
      if (tag === 'INPUT' && !['checkbox', 'radio', 'range', 'color', 'button', 'submit', 'reset'].includes(type)) return;
      if (el.isContentEditable) return;

      let target = el.closest(INTERACTIVE_SELECTOR);

      // Fallback: cursor:pointer ловит MUI и кастомные кликабельные элементы
      if (!target) {
        if (window.getComputedStyle(el).cursor !== 'pointer') return;
        target = el;
      }

      if (target.disabled || target.getAttribute('aria-disabled') === 'true') return;

      const count = 3 + Math.floor(Math.random() * 3);
      for (let i = 0; i < count; i++) {
        setTimeout(
          () => fireParticle(
            e.clientX + (Math.random() - 0.5) * 18,
            e.clientY + (Math.random() - 0.5) * 18,
          ),
          i * 55,
        );
      }
    };

    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('mouseover', handleMouseOver);
      clearTimeout(dwellTimer);
    };
  }, [active]);
}
